
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Image as ImageIcon } from 'lucide-react';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';

interface ImageUploadProps {
  id: string;
  label: string;
  currentImage?: string;
  onImageChange: (imageUrl: string) => void;
}

export const ImageUpload = ({ id, label, currentImage, onImageChange }: ImageUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentImage);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showWarningToast("File too large", "Please select an image under 5MB");
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      showWarningToast("Invalid file type", "Please select an image file");
      return;
    }

    setIsLoading(true);

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setPreviewUrl(result);
      onImageChange(result);
      setIsLoading(false);
    };
    reader.readAsDataURL(file);

    // Here you would normally upload to your server/cloud storage
    // Simulating upload process with timeout
    setTimeout(() => {
      showSuccessToast("Image uploaded", "Your image has been uploaded successfully");
    }, 1500);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 transition-all hover:border-cs-primary">
        {previewUrl ? (
          <div className="mb-4 relative">
            <img
              src={previewUrl}
              alt={label}
              className="max-h-48 rounded-lg object-cover shadow-md"
            />
          </div>
        ) : (
          <div className="mb-4 p-8 bg-gray-100 rounded-lg">
            <ImageIcon size={64} className="text-gray-400" />
          </div>
        )}
        
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => document.getElementById(id)?.click()}
            disabled={isLoading}
            className="relative"
          >
            <Upload className="mr-2 h-4 w-4" />
            {previewUrl ? "Change Image" : `Upload ${label}`}
            {isLoading && (
              <span className="absolute inset-0 flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-cs-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            )}
          </Button>
          
          <input
            type="file"
            id={id}
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <p className="mt-2 text-sm text-gray-500">
            Supported formats: JPG, PNG, GIF (max 5MB)
          </p>
        </div>
      </div>
    </div>
  );
};
